import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import type { BrandConfig, LegalConfig, Storefront } from '@kitup/brand';
import { App } from './App.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { BrandProvider } from './brand/BrandProvider.js';
import { I18nProvider } from './i18n/I18nProvider.js';
import type { DictionaryBuilders } from './i18n/registry.js';
import { initSentry } from './observability/sentry.js';
import './index.css';

/**
 * Mount the app under a specific brand. The brand, its legal bundle and its
 * locales' dictionaries are picked by the entry point (`apps/web-*`) — shared
 * code knows nothing about which brands exist, and does not import every
 * dictionary at once: otherwise the English-language brand would ship Russian
 * in its bundle.
 *
 * The legal bundle and the storefront are separate arguments rather than
 * fields of the brand because they are web-only: both are JSX, and the API
 * imports the brand. Here, where React is already loaded, that costs nothing.
 * The storefront has no default: every brand brings its own `/` and `/pricing`.
 *
 * `BrandProvider` sits above `I18nProvider` on purpose: dictionaries are built
 * from the brand, and without it there is nothing to assemble locales from.
 */
export function mount(
  brand: BrandConfig,
  legal: LegalConfig,
  storefront: Storefront,
  dictionaries: DictionaryBuilders,
): void {
  // Error tracking starts alongside the app without delaying the first frame.
  void initSentry();

  const container = document.getElementById('root');

  if (!container) {
    throw new Error('Не найден корневой элемент #root');
  }

  /**
   * Retries are off: 401s are handled by silent token refresh inside the client
   * (`api/client.ts`), and other API errors are not worth blindly retrying.
   */
  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  });

  createRoot(container).render(
    <StrictMode>
      <BrandProvider brand={brand} legal={legal} storefront={storefront}>
        <I18nProvider dictionaries={dictionaries}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </I18nProvider>
      </BrandProvider>
    </StrictMode>,
  );
}
